import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import {
  SORT_ORDER_LIKE_COUNT_DESC,
  SORT_ORDER_SESSION_COUNT_DESC,
  SORT_ORDER_UPDATE_DATE_DESC,
  SortOrder,
  sortOrders,
} from '../common/libs/sortOrder';
import { SlidePostBoostContentProps } from '../components/SlidePostBoostContent';

// クライアントサイドでarticlesをソートする
function getSorted(
  articles: SlidePostBoostContentProps['articles'],
  sortOrder: SortOrder,
) {
  if (!articles) return articles;
  const sortedArticles = [...(articles.articles ?? [])].sort((a, b) => {
    switch (sortOrder) {
      case SORT_ORDER_LIKE_COUNT_DESC:
        return b.likeCount - a.likeCount;
      case SORT_ORDER_SESSION_COUNT_DESC:
        return b.sessionCount - a.sessionCount;
      case SORT_ORDER_UPDATE_DATE_DESC:
      default: {
        const aTime = new Date(a.updateDate).getTime();
        const bTime = new Date(b.updateDate).getTime();
        return bTime - aTime;
      }
    }
  });
  return {
    ...articles,
    sortOrder,
    articles: sortedArticles,
  };
}

/**
 * SlidePostBoostコンポーネントで使う
 * スライド一覧をソートするクリックイベントとソートされたスライドのstateを返す
 */
export default function useSlidePostBoostArticles(
  inputArticles: SlidePostBoostContentProps['articles'],
): {
  articles: SlidePostBoostContentProps['articles'];
  onClickSortOrder: (order: SortOrder) => void;
} {
  const [articles, setArticles] = useState<
    SlidePostBoostContentProps['articles']
  >(inputArticles ?? null);
  const router = useRouter();

  const sortArticles = (order: SortOrder) => {
    setArticles((prevArticles) => getSorted(prevArticles, order));
  };

  // 読み込み時クエリがあればソートする
  useEffect(() => {
    const sortOrder = (router.query?.sortOrder ?? undefined) as
      | SortOrder
      | undefined;
    const isValid = sortOrder && sortOrders.includes(sortOrder);
    if (articles && isValid && sortOrder !== articles.sortOrder) {
      sortArticles(sortOrder);
    }
    // router.query.sortOrderの変更時のみ実行するため
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query?.sortOrder]);

  // クリック時、ソートしアドレスバーの該当パラメータのみ書き換える
  const onClickSortOrder = (order: SortOrder) => {
    sortArticles(order);
    router.replace(
      {
        pathname: router.pathname,
        query: { ...router.query, sortOrder: order },
      },
      undefined,
      { shallow: true },
    );
  };

  return useMemo(
    () => ({ articles, onClickSortOrder }),
    // onClickSortOrderは毎回新しい関数が生成されるため存配列に入れない
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [articles],
  );
}
